import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Buildings, Plus, PencilSimple, Trash, Users, Image as ImageIcon, Key, Check, X, LockOpen,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const LEEG = {
  naam: "", adres: "", stad: "", telefoon: "", email: "", kbo: "", rsz: "",
  vertegenwoordiger: "", iban: "", bic: "", paritair_comite: "", sector: "", website: "",
};

const VELDEN = [
  ["naam", "Firmanaam *"], ["adres", "Adres"], ["stad", "Postcode + stad"],
  ["kbo", "KBO / BTW-nummer"], ["rsz", "RSZ-nummer"], ["vertegenwoordiger", "Vertegenwoordiger"],
  ["telefoon", "Telefoon"], ["email", "E-mail"], ["iban", "IBAN"], ["bic", "BIC"],
  ["paritair_comite", "Paritair comité"], ["sector", "Sector"], ["website", "Website"],
];

const ROL_OPTIES = [
  { v: "admin", l: "Admin" },
  { v: "manager", l: "Manager" },
  { v: "verkoper", l: "Verkoper" },
  { v: "medewerker", l: "Medewerker" },
];

function FirmaDialog({ open, setOpen, firma, onSaved }) {
  const nieuw = !firma;
  const [form, setForm] = useState(LEEG);
  const [beheerder, setBeheerder] = useState({ admin_email: "", admin_wachtwoord: "", admin_naam: "" });
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(firma ? { ...LEEG, ...firma } : LEEG);
      setBeheerder({ admin_email: "", admin_wachtwoord: "", admin_naam: "" });
    }
  }, [open, firma]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const bewaar = async () => {
    if (!form.naam.trim()) return toast.error("Firmanaam is verplicht");
    if (nieuw && (!beheerder.admin_email || beheerder.admin_wachtwoord.length < 6))
      return toast.error("E-mail en wachtwoord (min. 6 tekens) van de beheerder zijn verplicht");
    setBezig(true);
    try {
      const payload = Object.fromEntries(VELDEN.map(([k]) => [k, form[k] || ""]));
      if (nieuw) {
        await axios.post(`${API}/firmas`, { ...payload, ...beheerder });
        toast.success("Firma aangemaakt met eigen beheerder");
      } else {
        await axios.put(`${API}/firmas/${firma.id}`, payload);
        toast.success("Firma bijgewerkt");
      }
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setBezig(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="firma-dialog">
        <DialogHeader>
          <DialogTitle>{nieuw ? "Nieuwe firma registreren" : `Firma bewerken — ${firma.naam}`}</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {VELDEN.map(([k, label]) => (
            <div key={k} className={k === "naam" || k === "adres" ? "sm:col-span-2" : ""}>
              <Label className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">{label}</Label>
              <Input value={form[k] || ""} onChange={set(k)} className="rounded-sm mt-1" data-testid={`firma-${k}-input`} />
            </div>
          ))}
        </div>

        {nieuw && (
          <div className="mt-4 border-t border-border pt-4">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#002FA7] mb-3">
              Beheerder van deze firma (login met e-mail)
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Naam</Label>
                <Input value={beheerder.admin_naam} onChange={(e) => setBeheerder((b) => ({ ...b, admin_naam: e.target.value }))} className="rounded-sm mt-1" data-testid="firma-admin-naam-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">E-mail *</Label>
                <Input type="email" value={beheerder.admin_email} onChange={(e) => setBeheerder((b) => ({ ...b, admin_email: e.target.value }))} className="rounded-sm mt-1" data-testid="firma-admin-email-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Wachtwoord *</Label>
                <Input type="text" value={beheerder.admin_wachtwoord} onChange={(e) => setBeheerder((b) => ({ ...b, admin_wachtwoord: e.target.value }))} className="rounded-sm mt-1" data-testid="firma-admin-wachtwoord-input" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Deze firma start met een volledig lege database: eigen filialen, medewerkers, uren en stock.
            </p>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)} data-testid="firma-cancel-btn">Annuleren</Button>
          <Button className="rounded-sm bg-[#002FA7] hover:bg-[#002280]" disabled={bezig} onClick={bewaar} data-testid="firma-save-btn">
            {bezig ? "Opslaan..." : "Opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GebruikersDialog({ firma, open, setOpen }) {
  const [users, setUsers] = useState([]);
  const [filialen, setFilialen] = useState([]);
  const [nieuw, setNieuw] = useState({ email: "", wachtwoord: "", naam: "", rol: "medewerker", filiaal_id: "" });
  const [resetId, setResetId] = useState("");
  const [resetPw, setResetPw] = useState("");

  const laden = useCallback(async () => {
    if (!firma) return;
    try {
      const [res, fils] = await Promise.all([
        axios.get(`${API}/firmas/${firma.id}/gebruikers`),
        axios.get(`${API}/filialen`, { params: { firma_id: firma.id } }),
      ]);
      setUsers(res.data);
      setFilialen(fils.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gebruikers laden mislukt");
    }
  }, [firma]);

  useEffect(() => { if (open) laden(); }, [open, laden]);

  const toevoegen = async () => {
    if (!nieuw.email || nieuw.wachtwoord.length < 6) return toast.error("E-mail en wachtwoord (min. 6 tekens) vereist");
    try {
      await axios.post(`${API}/firmas/${firma.id}/gebruikers`, {
        ...nieuw,
        filiaal_id: nieuw.filiaal_id || null,
      });
      toast.success("Gebruiker toegevoegd");
      setNieuw({ email: "", wachtwoord: "", naam: "", rol: "medewerker", filiaal_id: "" });
      laden();
    } catch (e) { toast.error(e.response?.data?.detail || "Toevoegen mislukt"); }
  };

  const reset = async (uid) => {
    if (resetPw.length < 6) return toast.error("Minstens 6 tekens");
    try {
      await axios.post(`${API}/firmas/${firma.id}/gebruikers/${uid}/wachtwoord`, { nieuw_wachtwoord: resetPw });
      toast.success("Wachtwoord gereset");
      setResetId(""); setResetPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset mislukt"); }
  };

  const verwijder = async (uid) => {
    try {
      await axios.delete(`${API}/firmas/${firma.id}/gebruikers/${uid}`);
      toast.success("Gebruiker verwijderd");
      laden();
    } catch (e) { toast.error(e.response?.data?.detail || "Verwijderen mislukt"); }
  };

  const wijzigRol = async (uid, rol) => {
    try {
      await axios.put(`${API}/firmas/${firma.id}/gebruikers/${uid}`, { rol });
      toast.success("Rol aangepast");
      laden();
    } catch (e) { toast.error(e.response?.data?.detail || "Aanpassen mislukt"); }
  };

  const deblokkeer = async (uid) => {
    try {
      const res = await axios.post(`${API}/firmas/${firma.id}/gebruikers/${uid}/deblokkeren`);
      toast.success(res.data.message || "Gedeblokkeerd");
      laden();
    } catch (e) { toast.error(e.response?.data?.detail || "Deblokkeren mislukt"); }
  };

  const wijzigFiliaal = async (uid, waarde) => {    try {
      await axios.put(`${API}/firmas/${firma.id}/gebruikers/${uid}`, {
        filiaal_id: waarde === "_geen" ? "" : waarde,
      });
      toast.success("Filiaal aangepast");
      laden();
    } catch (e) { toast.error(e.response?.data?.detail || "Aanpassen mislukt"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="gebruikers-dialog">
        <DialogHeader>
          <DialogTitle>Gebruikers — {firma?.naam}</DialogTitle>
        </DialogHeader>

        <div className="border border-border rounded-sm divide-y divide-border" data-testid="gebruikers-lijst">
          {users.length === 0 && <p className="p-4 text-sm text-muted-foreground">Nog geen gebruikers.</p>}
          {users.map((u) => (
            <div key={u.id} className="p-3 flex flex-wrap items-center gap-2" data-testid={`gebruiker-${u.id}`}>
              <div className="flex-1 min-w-[180px]">
                <p className="text-sm font-semibold">{u.naam || u.email}</p>
                <p className="text-xs text-muted-foreground">{u.email}</p>
                {u.geblokkeerd && (
                  <p className="text-xs font-bold text-[#C62828]" data-testid={`gebruiker-geblokkeerd-${u.id}`}>
                    Geblokkeerd — {u.pogingen} mislukte pogingen
                  </p>
                )}
              </div>
              <Select value={u.rol} onValueChange={(v) => wijzigRol(u.id, v)}>
                <SelectTrigger className="h-9 w-[130px] rounded-sm text-xs" data-testid={`gebruiker-rol-${u.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROL_OPTIES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={u.filiaal_id || "_geen"} onValueChange={(v) => wijzigFiliaal(u.id, v)}>
                <SelectTrigger className="h-9 w-[160px] rounded-sm text-xs" data-testid={`gebruiker-filiaal-${u.id}`}>
                  <SelectValue placeholder="Filiaal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_geen">Geen vast filiaal</SelectItem>
                  {filialen.map((f) => <SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>)}
                </SelectContent>
              </Select>
              {resetId === u.id ? (
                <div className="flex items-center gap-1">
                  <Input value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="nieuw wachtwoord" className="h-9 w-40 rounded-sm" data-testid={`reset-pw-input-${u.id}`} />
                  <Button size="sm" className="h-9 rounded-sm bg-[#38A169] hover:bg-[#2F855A]" onClick={() => reset(u.id)} data-testid={`reset-pw-save-${u.id}`}><Check size={16} weight="bold" /></Button>
                  <Button size="sm" variant="outline" className="h-9 rounded-sm" onClick={() => { setResetId(""); setResetPw(""); }}><X size={16} /></Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" className="h-9 rounded-sm" onClick={() => setResetId(u.id)} data-testid={`reset-pw-btn-${u.id}`}>
                  <Key size={14} weight="bold" className="mr-1" /> Wachtwoord
                </Button>
              )}
              {u.geblokkeerd && (
                <Button
                  size="sm"
                  className="h-9 rounded-sm bg-[#C62828] hover:bg-[#A31F1F] text-white"
                  onClick={() => deblokkeer(u.id)}
                  data-testid={`gebruiker-deblokkeer-${u.id}`}
                >
                  <LockOpen size={14} weight="bold" className="mr-1" /> Deblokkeren
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-9 rounded-sm text-[#E53E3E]" onClick={() => verwijder(u.id)} data-testid={`gebruiker-delete-${u.id}`}>
                <Trash size={14} weight="bold" />
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-4 border-t border-border pt-4">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#002FA7] mb-3">Gebruiker toevoegen</p>
          <div className="grid sm:grid-cols-4 gap-2">
            <Input placeholder="Naam" value={nieuw.naam} onChange={(e) => setNieuw((n) => ({ ...n, naam: e.target.value }))} className="rounded-sm" data-testid="nieuw-gebruiker-naam" />
            <Input placeholder="E-mail" type="email" value={nieuw.email} onChange={(e) => setNieuw((n) => ({ ...n, email: e.target.value }))} className="rounded-sm" data-testid="nieuw-gebruiker-email" />
            <Input placeholder="Wachtwoord" value={nieuw.wachtwoord} onChange={(e) => setNieuw((n) => ({ ...n, wachtwoord: e.target.value }))} className="rounded-sm" data-testid="nieuw-gebruiker-wachtwoord" />
            <Select value={nieuw.rol} onValueChange={(v) => setNieuw((n) => ({ ...n, rol: v }))}>
              <SelectTrigger className="rounded-sm" data-testid="nieuw-gebruiker-rol"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROL_OPTIES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select
              value={nieuw.filiaal_id || "_geen"}
              onValueChange={(v) => setNieuw((n) => ({ ...n, filiaal_id: v === "_geen" ? "" : v }))}
            >
              <SelectTrigger className="rounded-sm" data-testid="nieuw-gebruiker-filiaal">
                <SelectValue placeholder="Filiaal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_geen">Geen vast filiaal</SelectItem>
                {filialen.map((f) => <SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Kies je een filiaal, dan staat dat filiaal bij deze gebruiker automatisch ingesteld in Kassa / Verkocht (hij kan het wel nog zelf wijzigen).
          </p>
          <Button className="mt-3 rounded-sm bg-[#002FA7] hover:bg-[#002280]" onClick={toevoegen} data-testid="nieuw-gebruiker-btn">
            <Plus size={16} weight="bold" className="mr-1" /> Toevoegen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FirmaKaart({ firma, isSuperadmin, actief, onBewerk, onGebruikers, onLogo, onVerwijderLogo, onVerwijder, onKies }) {
  return (
    <div className={`bg-white border rounded-sm p-4 ${actief ? "border-[#002FA7] ring-1 ring-[#002FA7]/30" : "border-border"}`} data-testid={`firma-kaart-${firma.id}`}>
      <div className="flex items-start gap-3">
        {firma.logo_url ? (
          <img src={`${BACKEND}${firma.logo_url}`} alt={firma.naam} className="h-14 w-auto max-w-[170px] object-contain shrink-0" data-testid={`firma-logo-${firma.id}`} />
        ) : (
          <div className="w-14 h-14 rounded-sm bg-[#F4F5F7] border border-border flex items-center justify-center shrink-0">
            <Buildings size={24} className="text-muted-foreground" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-bold truncate" style={{ fontFamily: "Chivo, sans-serif" }}>{firma.naam}</h3>
            {actief && <Badge className="bg-[#002FA7] text-white text-[10px]">actief</Badge>}
          </div>
          <p className="text-xs text-muted-foreground truncate">{[firma.adres, firma.stad].filter(Boolean).join(", ") || "Geen adres"}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {firma.kbo ? `KBO ${firma.kbo}` : "Geen KBO"} · {firma.aantal_gebruikers ?? 0} gebruikers · {firma.aantal_medewerkers ?? 0} medewerkers
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {isSuperadmin && !actief && (
          <Button size="sm" className="rounded-sm h-9 bg-[#002FA7] hover:bg-[#002280]" onClick={() => onKies(firma)} data-testid={`firma-kies-${firma.id}`}>
            Werken in deze firma
          </Button>
        )}
        <Button size="sm" variant="outline" className="rounded-sm h-9" onClick={() => onBewerk(firma)} data-testid={`firma-bewerk-${firma.id}`}>
          <PencilSimple size={14} weight="bold" className="mr-1" /> Gegevens
        </Button>
        <Button size="sm" variant="outline" className="rounded-sm h-9" onClick={() => onGebruikers(firma)} data-testid={`firma-gebruikers-${firma.id}`}>
          <Users size={14} weight="bold" className="mr-1" /> Gebruikers
        </Button>
        <Button size="sm" variant="outline" className="rounded-sm h-9" onClick={() => onLogo(firma)} data-testid={`firma-logo-btn-${firma.id}`}>
          <ImageIcon size={14} weight="bold" className="mr-1" /> {firma.logo_url ? "Logo wijzigen" : "Logo"}
        </Button>
        {firma.logo_url && (
          <Button size="sm" variant="outline" className="rounded-sm h-9" onClick={() => onVerwijderLogo(firma)} data-testid={`firma-logo-verwijder-${firma.id}`}>
            Logo weg
          </Button>
        )}
        {isSuperadmin && (
          <Button size="sm" variant="outline" className="rounded-sm h-9 text-[#E53E3E]" onClick={() => onVerwijder(firma)} data-testid={`firma-verwijder-${firma.id}`}>
            <Trash size={14} weight="bold" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Firmas({ user, activeFirmaId, onKiesFirma, onFirmasChanged }) {
  const isSuperadmin = user?.rol === "superadmin";
  const [firmas, setFirmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bewerkFirma, setBewerkFirma] = useState(null);
  const [gebruikersFirma, setGebruikersFirma] = useState(null);
  const fileRef = useRef(null);
  const logoFirmaRef = useRef(null);

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/firmas`);
      setFirmas(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Firma's laden mislukt");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const kiesLogo = (firma) => { logoFirmaRef.current = firma; fileRef.current?.click(); };

  const uploadLogo = async (e) => {
    const input = e.target;
    const file = input.files?.[0];
    const firma = logoFirmaRef.current;
    if (!file || !firma) { input.value = ""; return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/firmas/${firma.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo opgeslagen");
      laden();
      onFirmasChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo uploaden mislukt");
    } finally {
      // Pas ná de upload leegmaken (iOS Safari maakt het bestand anders ongeldig)
      input.value = "";
    }
  };

  const verwijderLogo = async (firma) => {
    try {
      await axios.delete(`${API}/firmas/${firma.id}/logo`);
      toast.success("Logo verwijderd");
      laden();
      onFirmasChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo verwijderen mislukt");
    }
  };

  const verwijder = async (firma) => {
    if (!window.confirm(`Firma "${firma.naam}" verwijderen? Alle medewerkers, uren, planning en stock van deze firma worden definitief verwijderd.`)) return;
    try {
      await axios.delete(`${API}/firmas/${firma.id}?met_data=true`);
      toast.success("Firma verwijderd");
      if (activeFirmaId === firma.id) onKiesFirma(null);
      laden();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  return (
    <div className="space-y-6" data-testid="firmas-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Firma's</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isSuperadmin
              ? "Registreer en beheer al je bedrijven. Elke firma heeft een eigen login, eigen data en eigen logo."
              : "Bedrijfsgegevens en gebruikers van jouw firma."}
          </p>
        </div>
        {isSuperadmin && (
          <Dialog open={dialogOpen && !bewerkFirma} onOpenChange={(o) => { setDialogOpen(o); if (o) setBewerkFirma(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-[#002FA7] hover:bg-[#002280]" data-testid="nieuwe-firma-btn">
                <Plus size={18} weight="bold" className="mr-1" /> Nieuwe firma
              </Button>
            </DialogTrigger>
          </Dialog>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground" data-testid="firmas-loading">Laden...</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="firmas-grid">
          {firmas.map((f) => (
            <FirmaKaart
              key={f.id}
              firma={f}
              isSuperadmin={isSuperadmin}
              actief={activeFirmaId === f.id}
              onBewerk={(x) => { setBewerkFirma(x); setDialogOpen(true); }}
              onGebruikers={setGebruikersFirma}
              onLogo={kiesLogo}
              onVerwijderLogo={verwijderLogo}
              onVerwijder={verwijder}
              onKies={(x) => onKiesFirma(x.id)}
            />
          ))}
          {firmas.length === 0 && <p className="text-sm text-muted-foreground">Nog geen firma's geregistreerd.</p>}
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />

      <FirmaDialog
        open={dialogOpen}
        setOpen={(o) => { setDialogOpen(o); if (!o) setBewerkFirma(null); }}
        firma={bewerkFirma}
        onSaved={() => { laden(); onFirmasChanged?.(); }}
      />
      <GebruikersDialog
        firma={gebruikersFirma}
        open={!!gebruikersFirma}
        setOpen={(o) => { if (!o) setGebruikersFirma(null); }}
      />
    </div>
  );
}
